import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import SecurityStatusBadge from './SecurityStatusBadge';
import config from '../config';
import { QRCodeSVG } from 'qrcode.react';
import { firstScreenFor } from './Login';

const TenantLogin = () => {
    const { tenantId } = useParams();
    const navigate = useNavigate();
    const { login, verifyMfa, loginEnrollStart, loginEnrollConfirm } = useAuth();

    // MFA at workspace sign-in (same flow as Login.js): step 1 = email + password,
    // 3 = TOTP code challenge, 4 = forced enrolment (QR + first code), 5 = backup codes once.
    const [step, setStep] = useState(1);
    const [mfaToken, setMfaToken] = useState(null);
    const [mfaCode, setMfaCode] = useState('');
    const [enrollData, setEnrollData] = useState(null);        // { otpauth_uri, secret }
    const [enrollBackupCodes, setEnrollBackupCodes] = useState(null);

    // First screen after sign-in: the server's one-time onboarding pointer decides (retention setup or dashboard).
    const goToFirstScreen = () => navigate(firstScreenFor({ tenant_id: tenantId }));

    // Read admin_token from localStorage for "Continue as Admin" feature
    const adminToken = localStorage.getItem('admin_token');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [autoImpersonating, setAutoImpersonating] = useState(false);

    // Auto-impersonate for SuperAdmins — no login form needed
    useEffect(() => {
        if (adminToken && !autoImpersonating) {
            setAutoImpersonating(true);
            handleContinueAsAdmin();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await login(email, password, tenantId);
            if (result.success && result.mfaRequired) {
                // Password OK but 2FA needed: no session exists yet.
                setMfaToken(result.mfaToken);
                setMfaCode('');
                if (result.mfaEnrollRequired) {
                    // Not enrolled (e.g. invited user at first sign-in): enrol before access.
                    setStep(4);
                    handleStartForcedEnroll(result.mfaToken);
                } else {
                    setStep(3);   // normal code challenge (already enrolled)
                }
                return;
            }
            if (result.success) {
                goToFirstScreen();
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError("Login failed");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyMfa = async (e) => {
        e.preventDefault();
        setError(''); setLoading(true);
        try {
            const res = await verifyMfa(mfaCode.trim(), mfaToken);
            if (res.success) goToFirstScreen();
            else setError(res.error || 'Invalid code. Please try again.');
        } catch (err) {
            setError('Invalid code. Please try again.');
        } finally { setLoading(false); }
    };

    // Forced enrolment: fetch a QR, then confirm the first code.
    const handleStartForcedEnroll = async (token) => {
        setError(''); setLoading(true);
        try {
            const data = await loginEnrollStart(token);
            setEnrollData(data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not start enrollment.');
        } finally { setLoading(false); }
    };

    const handleConfirmForcedEnroll = async (e) => {
        e.preventDefault();
        setError(''); setLoading(true);
        try {
            const res = await loginEnrollConfirm(mfaToken, mfaCode.trim());
            if (!res.success) { setError(res.error || 'Invalid code.'); return; }
            // Session is now set; show backup codes on step 5 before navigating.
            setEnrollBackupCodes(res.backupCodes || []);
            setStep(5);
        } catch (err) {
            setError('Invalid code. Please try again.');
        } finally { setLoading(false); }
    };

    const handleContinueAsAdmin = async () => {
        // A1 (owner 2026-10-10): support access needs a reason (append-only audit).
        const reason = window.prompt('Reason for accessing this workspace (visible to its admins):');
        if (!reason || !reason.trim()) {
            setAutoImpersonating(false);
            setError('A reason is required to continue as Admin.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await axios.post(`${config.API_BASE_URL}/auth/impersonate`,
                { tenant_id: tenantId, reason: reason.trim() },
                { headers: { Authorization: `Bearer ${adminToken}` } }
            );

            // Cookie is set automatically by browser from the response

            // Log impersonation event
            console.log(`[AUDIT] SuperAdmin impersonated tenant: ${tenantId}`);

            // Force reload to pick up new context
            window.location.href = `/t/${tenantId}/dashboard`;

        } catch (err) {
            console.error("Impersonation failed:", err);
            setAutoImpersonating(false);
            const msg = err.response?.data?.detail || err.message || "Failed to continue as Admin.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    // Show loading screen while auto-impersonating
    if (autoImpersonating && !error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Entering workspace...</p>
                    <p className="text-gray-400 text-sm mt-1">{tenantId}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 border border-gray-100">
                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">Workspace Access</h2>
                    <p className="text-sm text-gray-500 mt-2">
                        Sign in to <span className="font-mono text-blue-600 font-bold">{tenantId}</span>
                    </p>
                </div>

                {adminToken && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg text-center">
                        <p className="text-sm text-blue-800 mb-3">You are logged in as Super Admin</p>
                        <button
                            onClick={handleContinueAsAdmin}
                            disabled={loading}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded-lg transition-colors shadow-sm"
                        >
                            Continue as Admin
                        </button>
                        <div className="relative my-4">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-blue-50 text-gray-500">Or sign in as user</span>
                            </div>
                        </div>
                    </div>
                )}

                {step !== 1 && error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded text-sm border border-red-100 break-words mb-4">
                        <strong>Error: </strong> {error}
                    </div>
                )}

                {step === 3 && (
                    <form onSubmit={handleVerifyMfa}>
                        <div className="mb-2">
                            <label htmlFor="tenant-mfa-code" className="block text-xs font-bold text-gray-700 uppercase mb-2">Two-Factor Authentication</label>
                            <p className="text-xs text-gray-500 mb-3">Enter the 6-digit code from your authenticator app, or a backup code.</p>
                            <input
                                id="tenant-mfa-code"
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                value={mfaCode}
                                onChange={(e) => setMfaCode(e.target.value.replace(/\s/g, ''))}
                                className="w-full text-center tracking-widest font-mono text-lg border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 outline-none"
                                placeholder="123456"
                                required
                                autoFocus
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading || !mfaCode.trim()}
                            className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50 mt-2"
                        >
                            {loading ? 'Verifying...' : 'Verify'}
                        </button>
                        <div className="text-center mt-3">
                            <button
                                type="button"
                                onClick={() => { setStep(1); setMfaCode(''); setMfaToken(null); setError(''); }}
                                className="text-sm text-gray-500 hover:text-gray-700"
                            >
                                Back
                            </button>
                        </div>
                    </form>
                )}

                {step === 4 && (
                    <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1">Set up Two-Factor Authentication</h3>
                        <p className="text-xs text-gray-500 mb-4">Your organization requires 2FA. Scan the QR code with an authenticator app, then enter the 6-digit code.</p>
                        {enrollData ? (
                            <>
                                <div className="flex justify-center mb-3">
                                    <div className="bg-white p-3 border border-gray-200 rounded-lg">
                                        <QRCodeSVG value={enrollData.otpauth_uri} size={168} />
                                    </div>
                                </div>
                                <p className="text-[11px] text-gray-500 text-center mb-1">Can't scan? Enter this key:</p>
                                <p className="font-mono text-[11px] text-center bg-gray-50 border border-gray-200 rounded px-2 py-1 break-all mb-3">{enrollData.secret}</p>
                                <form onSubmit={handleConfirmForcedEnroll}>
                                    <input value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        aria-label="Authenticator code"
                                        inputMode="numeric" autoComplete="one-time-code" placeholder="123456" autoFocus
                                        className="w-full text-center tracking-widest font-mono text-lg border border-gray-300 rounded-lg p-3 mb-2 outline-none focus:ring-2 focus:ring-blue-500" />
                                    <button type="submit" disabled={loading || mfaCode.length < 6}
                                        className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50">
                                        {loading ? 'Verifying...' : 'Verify & Enable'}
                                    </button>
                                </form>
                            </>
                        ) : (
                            <p className="text-sm text-gray-500 text-center py-6">Preparing enrollment…</p>
                        )}
                    </div>
                )}

                {step === 5 && (
                    <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1">Save your backup codes</h3>
                        <p className="text-xs text-gray-500 mb-3">Each code works once. Store them somewhere safe — they're the only way in if you lose your phone. They won't be shown again.</p>
                        <div className="grid grid-cols-2 gap-2 font-mono text-sm mb-4">
                            {(enrollBackupCodes || []).map(c => <div key={c} className="bg-gray-50 border border-gray-200 rounded px-3 py-1.5 text-center">{c}</div>)}
                        </div>
                        <button onClick={goToFirstScreen}
                            className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700">
                            I've saved them — Continue
                        </button>
                    </div>
                )}

                {step === 1 && (
                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="bg-red-50 text-red-600 p-3 rounded text-sm border border-red-100 break-words">
                            <strong>Error: </strong> {error}
                            {error.includes("Login failed") && (
                                <div className="mt-2 text-xs text-gray-500">
                                    Check console (F12) for details.
                                    <br />Target: {config.API_BASE_URL}
                                </div>
                            )}
                        </div>
                    )}
                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Email</label>
                        <input
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="user@example.com"
                            type="email"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2">Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="••••••••"
                            required
                        />
                    </div>
                    <button
                        disabled={loading}
                        className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50"
                    >
                        {loading ? 'Authenticating...' : 'Access Workspace'}
                    </button>

                    <div className="text-center mt-4">
                        <button
                            type="button"
                            onClick={() => {
                                if (adminToken) {
                                    // Cookie-based auth: navigate back without setting token
                                    window.location.href = '/super-admin';
                                } else {
                                    navigate('/login');
                                }
                            }}
                            className="text-xs text-gray-400 hover:text-gray-600 underline"
                        >
                            {adminToken ? 'Return to Global Console' : 'Back to Login'}
                        </button>
                    </div>
                </form>
                )}
                <SecurityStatusBadge />
            </div>
        </div>
    );
};

export default TenantLogin;
